
import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useTransform, animate, AnimatePresence } from 'framer-motion';
import {
    Check,
    X,
    MessageSquare,
    Rocket,
    Building2,
    Calculator,
    ChevronRight,
    Headphones,
    MessageCircle,
    Smartphone,
    CreditCard,
    Banknote,
    QrCode,
    Shield,
    Lock,
    Globe,
    Zap,
    Phone,
    Mail,
    HelpCircle,
    ChevronDown,
    Sparkles,
    BadgeCheck,
    Clock,
    Users,
    BarChart,
    Plus,
    Minus,
    Target,
    ShieldCheck,
    Zap as ZapIcon,
    Globe as GlobeIcon,
    MessageSquare as MessageSquareIcon
} from 'lucide-react';


import SIDE_IMAGE from "../../assets/pricing.webp"
import TEAM_IMAGE from "../../assets/pricing2.webp"
import PAYMENT_BG_IMAGE from "../../assets/pricing3.avif"
import SUPPORT_BG_IMAGE from "../../assets/pricing4.webp"


// const SIDE_IMAGE = "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=1632&auto=format&fit=crop";
// const TEAM_IMAGE = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070&auto=format&fit=crop";
// FIXED: New reliable Payment Terminal Image
// const PAYMENT_BG_IMAGE = "https://images.unsplash.com/photo-1556742111-a301076d9d18?q=80&w=2070&auto=format&fit=crop";
// NEW: Support CTA Background - Professional Office/Team Image
// const SUPPORT_BG_IMAGE = "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=2000&auto=format&fit=crop";

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.15 }
    }
};

const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { type: "spring", stiffness: 120, damping: 20 }
    },
    hover: {
        y: -8,
        boxShadow: "0px 20px 40px -10px rgba(0, 0, 0, 0.15)",
        transition: { duration: 0.25 }
    }
};

// Smooth Left-to-Right for Top Image
const imageLeftToRightVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 1, ease: [0.25, 0.1, 0.25, 1] }
    }
};

// Smooth Right-to-Left for Bottom Image
const imageRightToLeftVariants = {
    hidden: { opacity: 0, x: 100 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 1, ease: [0.25, 0.1, 0.25, 1], delay: 0.2 }
    }
};

// Smooth Left-to-Right for Text
const textSlideVariants = {
    hidden: { opacity: 0, x: -60 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.8, ease: "easeOut" }
    }
};

const roamingIconVariants = {
    animate: {
        rotate: 360,
        y: [0, -4, 0],
        transition: {
            rotate: { duration: 7, repeat: Infinity, ease: "linear" },
            y: { duration: 1.8, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }
        }
    }
};

// FAQ Animation Variants
const faqItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: "easeOut" }
    }
};

const faqContentVariants = {
    collapsed: {
        height: 0,
        opacity: 0,
        transition: { duration: 0.3, ease: "easeInOut" }
    },
    expanded: {
        height: "auto",
        opacity: 1,
        transition: { duration: 0.4, ease: "easeInOut" }
    }
};

// --- Helper Components ---

const FeatureItem = ({ text, included = true }) => (
    <div className="flex items-start space-x-2.5 mb-2.5">
        <div className={`mt-0.5 min-w-[18px] ${included ? 'text-[#0C4550]' : 'text-gray-300'}`}>
            {included ? <Check size={14} /> : <X size={14} />}
        </div>
        <span className={`text-xs ${included ? 'text-gray-700 font-medium' : 'text-gray-400 line-through'}`}>
            {text}
        </span>
    </div>
);

const PricingCard = ({ title, price, features, recommended = false, icon: Icon }) => {
    return (
        <motion.div
            variants={cardVariants}
            whileHover="hover"
            className={`relative p-6 rounded-xl border transition-all duration-250 bg-white flex flex-col h-full
        ${recommended
                    ? 'border-[#0C4550] shadow-lg ring-1 ring-[#0C4550]/20'
                    : 'border-gray-200 shadow-md hover:border-blue-300'}
      `}
        >
            {recommended && (
                <div className="absolute top-0 right-0 bg-[#F46653] text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg shadow-sm z-10">
                    MOST POPULAR
                </div>
            )}

            <div className="mb-5 relative z-10">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 shadow-inner
          ${recommended ? 'bg-gradient-to-br from-[#0C4550] to-[#092c33] text-white' : 'bg-gradient-to-br from-blue-50 to-blue-100 text-[#0C4550]'}`}>

                    <motion.div variants={roamingIconVariants} animate="animate">
                        <Icon size={22} />
                    </motion.div>

                </div>

                <h3 className="text-xl font-bold text-gray-800 tracking-tight">{title}</h3>
                <div className="mt-3 flex items-baseline">
                    <span className="text-4xl font-black text-[#0C4550] tracking-tighter drop-shadow-sm">{price}</span>
                    <span className="ml-1.5 text-gray-500 font-semibold text-sm">/ SMS</span>
                </div>
                <p className="mt-1.5 text-[10px] text-gray-400 font-medium">
                    * GST extra as applicable.
                </p>
            </div>

            <div className="flex-grow">
                <div className="h-px w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-5"></div>
                {features.map((feature, idx) => (
                    <FeatureItem key={idx} text={feature.text} included={feature.included} />
                ))}
            </div>

            <button className={`w-full mt-6 py-3 px-5 rounded-lg font-bold transition-all duration-250 transform active:scale-95 text-sm flex items-center justify-center gap-2
        ${recommended
                    ? 'bg-[#0C4550] text-white hover:bg-[#083038] shadow-md hover:shadow-[#0C4550]/30'
                    : 'bg-white text-[#0C4550] border-2 border-[#0C4550] hover:bg-blue-50'}
      `}>
                Choose Plan
            </button>
        </motion.div>
    );
};

// --- Counter Animation Component ---
const Counter = ({ value, prefix = "", suffix = "" }) => {
    const nodeRef = useRef();

    useEffect(() => {
        const node = nodeRef.current;
        if (!node) return;

        const controls = animate(0, value, {
            duration: 0.5,
            onUpdate: (latest) => {
                if (Number.isInteger(value)) {
                    node.textContent = prefix + Math.round(latest).toLocaleString() + suffix;
                } else {
                    node.textContent = prefix + latest.toFixed(2) + suffix;
                }
            }
        });

        return () => controls.stop();
    }, [value, prefix, suffix]);

    return <span ref={nodeRef} />;
};

// --- Floating Bubble Animation Component ---
const FloatingBubble = ({ delay, xPos }) => (
    <motion.div
        initial={{ opacity: 0, y: 100, x: xPos }}
        animate={{
            opacity: [0, 1, 1, 0],
            y: -150,
            x: xPos + (Math.random() * 40 - 20)
        }}
        transition={{
            duration: 4,
            repeat: Infinity,
            delay: delay,
            ease: "easeOut"
        }}
        className="absolute bottom-10 z-20 pointer-events-none"
    >
        <div className="bg-white/90 backdrop-blur-md text-[#0C4550] p-2 rounded-2xl rounded-bl-none shadow-lg border border-[#F46653]/30">
            <MessageSquare size={20} />
        </div>
    </motion.div>
);

// --- Interactive Calculator Section ---
const InteractiveCalculator = () => {
    const [sliderValue, setSliderValue] = useState(25);

    // Logic: Range 5,000 to 200,000 SMS
    const minSMS = 5000;
    const maxSMS = 200000;
    const smsCount = Math.round(((maxSMS - minSMS) * (sliderValue / 100)) + minSMS);

    // Dynamic Price Tier Logic
    const getRate = (count) => {
        if (count < 25000) return 0.15;
        if (count < 75000) return 0.13;
        if (count < 150000) return 0.11;
        return 0.10; // Best rate
    };

    const rate = getRate(smsCount);
    const totalCost = Math.round(smsCount * rate);

    return (
        <section className="relative w-full py-16 px-4 md:px-8 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
            <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

                    {/* Left: Calculator Control */}
                    <div className="space-y-8 z-10">
                        <div className="space-y-2">
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                className="inline-flex items-center gap-2 bg-[#0C4550]/10 text-[#0C4550] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                            >
                                <Calculator size={14} className="text-[#F46653]" />
                                <span>Flexi-Estimator</span>
                            </motion.div>

                            {/* Heading Animation: Left to Right */}
                            <motion.h2
                                variants={textSlideVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.5 }}
                                className="text-3xl md:text-5xl font-extrabold text-gray-800 leading-tight"
                            >
                                Scale your reach,<br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0C4550] to-[#0c4550]/70">
                                    adjust your budget.
                                </span>
                            </motion.h2>

                            <p className="text-gray-600 text-lg">
                                Drag the slider to customize your credit package. Higher volumes unlock exclusive enterprise rates.
                            </p>
                        </div>

                        {/* Interactive Card */}
                        <motion.div
                            className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 relative overflow-hidden"
                            whileHover={{ boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.1)" }}
                        >
                            {/* SMS Count Display */}
                            <div className="flex justify-between items-end mb-6">
                                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Volume</div>
                                <div className="text-3xl font-black text-[#0C4550]">
                                    <Counter value={smsCount} />
                                    <span className="text-base text-gray-400 font-medium ml-1">SMS</span>
                                </div>
                            </div>

                            {/* Custom Slider */}
                            <div className="relative h-12 flex items-center mb-8">
                                <div className="absolute w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                                    <motion.div
                                        className="h-full bg-gradient-to-r from-[#F46653] to-[#ff8c7d]"
                                        style={{ width: `${sliderValue}%` }}
                                    />
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={sliderValue}
                                    onChange={(e) => setSliderValue(e.target.value)}
                                    className="absolute w-full h-full opacity-0 cursor-pointer z-20"
                                />
                                {/* Custom Thumb Representation */}
                                <motion.div
                                    className="absolute h-8 w-8 bg-white border-4 border-[#F46653] rounded-full shadow-lg z-10 flex items-center justify-center pointer-events-none"
                                    style={{ left: `calc(${sliderValue}% - 16px)` }}
                                    animate={{ scale: [1, 1.1, 1] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                >
                                    <div className="w-2 h-2 bg-[#0C4550] rounded-full"></div>
                                </motion.div>
                            </div>

                            {/* Calculation Details Grid */}
                            <div className="grid grid-cols-2 gap-4 mb-8">
                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                    <p className="text-xs text-gray-500 mb-1">Rate per SMS</p>
                                    <div className="text-xl font-bold text-gray-800 flex items-center">
                                        ₹<Counter value={rate} suffix="" />
                                        {rate < 0.15 && (
                                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">
                                                SAVE {Math.round((0.15 - rate) / 0.15 * 100)}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="bg-[#0C4550] p-4 rounded-xl shadow-lg transform scale-105 border border-[#1a5f6c]">
                                    <p className="text-xs text-white/70 mb-1">Total Estimate</p>
                                    <div className="text-2xl font-black text-white">
                                        ₹<Counter value={totalCost} />
                                    </div>
                                </div>
                            </div>

                            <button className="w-full py-4 bg-[#F46653] hover:bg-[#d65544] text-white font-bold rounded-xl transition-all active:scale-95 flex items-center justify-center gap-2 group">
                                <span>Get This Package</span>
                                <ChevronRight className="group-hover:translate-x-1 transition-transform" />
                            </button>

                            <p className="text-center mt-4 text-xs text-gray-500">
                                No hidden setup fees. Instant activation.
                            </p>
                        </motion.div>
                    </div>

                    {/* Right: Immersive Image - NO BACKGROUND COLOR OVERLAY */}
                    <motion.div
                        variants={imageRightToLeftVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        className="relative h-[450px] lg:h-[520px] w-full"
                    >
                        <div className="w-full h-full relative">
                            {/* Container */}
                            <div
                                className="absolute inset-0 rounded-[2rem] overflow-hidden shadow-2xl z-0"
                                style={{
                                    transformStyle: "preserve-3d",
                                    perspective: "1000px",
                                    transformOrigin: "center center",
                                    boxShadow: "0 50px 100px -20px rgba(12, 69, 80, 0.3), 0 30px 60px -40px rgba(244, 102, 83, 0.2)"
                                }}
                            >
                                {/* Image - Clean without overlay */}
                                <div className="absolute inset-0">
                                    <img
                                        src={TEAM_IMAGE}
                                        alt="Team Connecting"
                                        className="w-full h-full object-cover"
                                    />
                                </div>

                                {/* Floating SMS Animation Flow */}
                                <FloatingBubble delay={0} xPos={40} />
                                <FloatingBubble delay={1.5} xPos={120} />
                                <FloatingBubble delay={2.8} xPos={80} />
                                <FloatingBubble delay={0.5} xPos={180} />
                                <FloatingBubble delay={2.2} xPos={150} />

                                {/* Glassmorphism Badge */}
                                <div
                                    className="absolute bottom-8 right-6 bg-white/20 backdrop-blur-lg border border-white/30 p-3 rounded-2xl max-w-[180px] text-white shadow-xl"
                                >
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <div className="bg-[#F46653] p-1.5 rounded-lg">
                                            <Rocket size={12} className="text-white" />
                                        </div>
                                        <span className="font-bold text-xs">Turbo Speed</span>
                                    </div>
                                    <p className="text-[11px] text-white/90 leading-relaxed">
                                        Our infrastructure handles 5M+ SMS/hour with zero lag.
                                    </p>
                                </div>
                            </div>

                            {/* Glow Effect - Subtle */}
                            <div
                                className="absolute -z-10 inset-0 bg-gradient-to-r from-[#0C4550]/10 to-[#F46653]/5 blur-2xl rounded-full opacity-30 scale-110"
                            />
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

// --- Payment Methods Section (DARK THEME & VISIBLE IMAGE) ---
const PaymentMethods = () => {
    const paymentOptions = [
        {
            icon: CreditCard,
            title: "Credit/Debit Cards",
            description: "Visa, MasterCard, Rupay, American Express",
            color: "from-blue-500 to-purple-600",
            features: ["Instant Processing", "Secure 3D Authentication", "No Extra Charges"],
            delay: 0
        },
        {
            icon: Banknote,
            title: "Net Banking",
            description: "All Major Indian Banks Supported",
            color: "from-green-500 to-emerald-600",
            features: ["Direct Bank Transfer", "Real-time Confirmation", "24/7 Available"],
            delay: 0.1
        },
        {
            icon: QrCode,
            title: "UPI & QR Payments",
            description: "Google Pay, PhonePe, Paytm, BHIM",
            color: "from-[#0C4550] to-[#1a5f6c]",
            features: ["Instant Settlement", "Scan & Pay", "No Transaction Fees"],
            delay: 0.2
        },
        {
            icon: Smartphone,
            title: "Mobile Wallets",
            description: "Paytm, MobiKwik, Amazon Pay",
            color: "from-orange-500 to-[#F46653]",
            features: ["One-Tap Payment", "Cashback Offers", "Wallet Balance"],
            delay: 0.3
        }
    ];

    return (
        <section className="relative w-full py-20 px-4 md:px-8 overflow-hidden">

            {/* --- VISIBLE PROFESSIONAL BACKGROUND --- */}
            <div className="absolute inset-0 z-0">
                {/* 1. The Real Image (No Blur) - Using a new reliable ID */}
                <img
                    src={PAYMENT_BG_IMAGE}
                    alt="Secure Payments"
                    className="w-full h-full object-cover opacity-100"
                />

                {/* 2. SIMPLE OVERLAY: Dark simple overlay. 
                    Removed 'mix-blend-multiply' and 'bg-gray-900' from parent to guarantee visibility.
                */}
                <div className="absolute inset-0 bg-black/60"></div>

                {/* 3. Gradient for Depth */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20"></div>
            </div>

            {/* Floating Elements (Light color for dark background) */}
            <motion.div
                animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-20 left-10 text-white/5 z-10"
            >
                <CreditCard size={120} />
            </motion.div>
            <motion.div
                animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                className="absolute bottom-20 right-10 text-white/5 z-10"
            >
                <Banknote size={100} />
            </motion.div>

            <div className="max-w-6xl mx-auto relative z-20">
                {/* Header (Text switched to WHITE for dark background) */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-4 border border-white/20 shadow-sm">
                        <Lock className="text-[#F46653]" size={16} />
                        <span className="text-sm font-bold text-white uppercase tracking-wider">
                            SECURE PAYMENTS
                        </span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4 drop-shadow-lg">
                        Multiple &{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4ade80] to-[#2dd4bf]">
                            Secure Payment Options
                        </span>
                    </h2>
                    <p className="text-gray-300 text-lg max-w-2xl mx-auto font-medium">
                        Choose your preferred payment method. All transactions are encrypted with 256-bit SSL security and processed instantly.
                    </p>
                </motion.div>

                {/* Payment Options Grid */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
                >
                    {paymentOptions.map((option, index) => (
                        <motion.div
                            key={index}
                            variants={cardVariants}
                            custom={index}
                            whileHover={{
                                y: -12,
                                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.3)",
                                transition: { duration: 0.3 }
                            }}
                            className="group relative"
                        >
                            {/* Card Background - SOLID WHITE for maximum contrast against dark bg */}
                            <div className="bg-white rounded-2xl p-6 border border-white/10 shadow-xl hover:shadow-2xl transition-all duration-300 h-full relative overflow-hidden">

                                {/* Icon with Gradient Background */}
                                <motion.div
                                    whileHover={{ scale: 1.1, rotate: 5 }}
                                    transition={{ duration: 0.3 }}
                                    className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${option.color} p-4 flex items-center justify-center mb-6 shadow-md`}
                                >
                                    <option.icon className="text-white" size={28} />
                                    <div className="absolute inset-0 bg-white/10 rounded-2xl"></div>
                                </motion.div>

                                <h3 className="text-xl font-bold text-gray-900 mb-2 relative z-10">{option.title}</h3>
                                <p className="text-gray-600 text-sm mb-4 relative z-10">{option.description}</p>

                                <div className="space-y-2 relative z-10">
                                    {option.features.map((feature, idx) => (
                                        <motion.div
                                            key={idx}
                                            initial={{ opacity: 0, x: -10 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            transition={{ delay: idx * 0.1 }}
                                            className="flex items-center gap-2"
                                        >
                                            <motion.div
                                                whileHover={{ scale: 1.2 }}
                                                className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center"
                                            >
                                                <Check className="text-green-600" size={12} />
                                            </motion.div>
                                            <span className="text-sm text-gray-700">{feature}</span>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Security Note */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    className="mt-12 text-center"
                >
                    <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-full shadow-sm">
                        <Shield className="text-[#F46653]" size={20} />
                        <span className="text-sm text-gray-200 font-semibold">
                            All payments are PCI DSS compliant and secured with 256-bit encryption
                        </span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

// --- FAQ Section (Updated with 6 questions and F0EFEB background) ---
const FAQSection = () => {
    const [activeIndex, setActiveIndex] = useState(null);

    const faqs = [
        {
            question: "What is the minimum SMS quantity I can purchase?",
            answer: "You can start with as low as 500 SMS credits. No minimum commitment required for standard plans.",
            icon: MessageSquareIcon,
            category: "Pricing"
        },
        {
            question: "How quickly are SMS messages delivered?",
            answer: "Transactional SMS are delivered within 2-5 seconds. Promotional SMS follow TRAI guidelines and deliver within minutes.",
            icon: ZapIcon,
            category: "Delivery"
        },
        {
            question: "Do you provide delivery reports?",
            answer: "Yes, real-time delivery reports with sent, delivered, and failed statuses are available in your dashboard.",
            icon: ShieldCheck,
            category: "Reports"
        },
        {
            question: "Can I use my own sender ID?",
            answer: "Yes, both promotional and transactional sender IDs are available after verification and approval.",
            icon: Target,
            category: "Features"
        },
        {
            question: "Is there an API available?",
            answer: "Yes, we provide REST API with comprehensive documentation for seamless integration.",
            icon: GlobeIcon,
            category: "Integration"
        },
        {
            question: "What payment methods do you accept?",
            answer: "We accept all major payment methods including cards, net banking, UPI, and mobile wallets.",
            icon: CreditCard,
            category: "Payments"
        }
    ];

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <section className="relative w-full py-20 px-4 md:px-8 overflow-hidden" style={{ backgroundColor: '#F0EFEB' }}>
            {/* Background Pattern - Subtle */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230C4550' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    backgroundSize: '40px 40px'
                }}></div>
            </div>

            {/* Floating Elements */}
            <motion.div
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-1/4 left-10 text-[#0C4550]/10"
            >
                <HelpCircle size={50} />
            </motion.div>

            <motion.div
                animate={{ y: [0, 20, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute bottom-1/4 right-10 text-[#F46653]/10"
            >
                <HelpCircle size={40} />
            </motion.div>

            <div className="max-w-5xl mx-auto relative z-10">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mb-12"
                >
                    <motion.div
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200 }}
                        className="inline-flex items-center gap-3 bg-gradient-to-r from-[#0C4550] to-[#1a5f6c] px-6 py-3 rounded-full mb-6 shadow-lg"
                    >
                        <HelpCircle className="text-white" size={20} />
                        <span className="text-sm font-bold text-white uppercase tracking-wider">
                            FREQUENTLY ASKED QUESTIONS
                        </span>
                    </motion.div>

                    <h2 className="text-4xl md:text-5xl font-extrabold text-gray-800 mb-6">
                        Got questions? We've got{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0C4550] to-[#F46653]">
                            answers.
                        </span>
                    </h2>

                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="text-gray-600 text-lg max-w-3xl mx-auto"
                    >
                        Quick answers to common questions about Eurofox Bulk SMS services.
                    </motion.p>
                </motion.div>

                {/* FAQ Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-4xl mx-auto">
                    {faqs.map((faq, index) => (
                        <motion.div
                            key={index}
                            variants={faqItemVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            custom={index}
                            className="relative"
                        >
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
                                <button
                                    onClick={() => toggleFAQ(index)}
                                    className="w-full p-5 text-left flex items-center justify-between group hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center gap-4 flex-1">
                                        <div className={`p-2.5 rounded-lg transition-all ${activeIndex === index
                                            ? 'bg-[#0C4550] text-white'
                                            : 'bg-[#F0EFEB] text-[#0C4550] group-hover:bg-[#0C4550] group-hover:text-white'
                                            }`}>
                                            <faq.icon size={18} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <p className="text-base font-semibold text-gray-800 group-hover:text-[#0C4550] transition-colors">
                                                    {faq.question}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <motion.div
                                        animate={{ rotate: activeIndex === index ? 180 : 0 }}
                                        transition={{ duration: 0.3 }}
                                        className={`ml-3 p-1.5 rounded-lg transition-all ${activeIndex === index
                                            ? 'bg-[#0C4550] text-white'
                                            : 'bg-gray-100 text-gray-600 group-hover:bg-[#0C4550] group-hover:text-white'
                                            }`}
                                    >
                                        <ChevronDown size={18} />
                                    </motion.div>
                                </button>
                                <AnimatePresence>
                                    {activeIndex === index && (
                                        <motion.div
                                            variants={faqContentVariants}
                                            initial="collapsed"
                                            animate="expanded"
                                            exit="collapsed"
                                            className="overflow-hidden"
                                        >
                                            <div className="px-5 pb-5">
                                                <div className="pl-14">
                                                    <div className="bg-[#F8F8F6] rounded-lg p-4 border-l-4 border-[#0C4550]">
                                                        <p className="text-sm text-gray-700 leading-relaxed line-clamp-2">
                                                            {faq.answer}
                                                        </p>
                                                        <div className="mt-2">
                                                            <span className="text-xs font-medium text-[#0C4550] bg-[#0C4550]/10 px-2 py-1 rounded">
                                                                {faq.category}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    ))}
                </div>
                {/* CTA Section - Improved Visibility */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-16 text-center"
                >
                    <div className="rounded-3xl p-10 border border-white/20 shadow-2xl relative overflow-hidden group bg-white/80 backdrop-blur-xl isolate">

                        {/* Background Image */}
                        <div className="absolute inset-0 -z-10">
                            <img
                                src={SUPPORT_BG_IMAGE}
                                alt="Support Team Background"
                                className="
          w-full h-full object-cover
          opacity-60
          blur-[0.5px]
          scale-105
          transition-transform duration-700 group-hover:scale-110
        "
                            />
                            <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-black/50 to-transparent" />
                        </div>

                        {/* Background Pattern */}
                        <div className="absolute inset-0 -z-10 pointer-events-none opacity-[0.06] mix-blend-overlay">
                            <div
                                className="absolute inset-0"
                                style={{
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E")`,
                                    backgroundSize: "48px 48px"
                                }}
                            />
                        </div>

                        {/* Content */}
                        <div className="relative z-10">
                            {/* Badge */}
                            <motion.div
                                initial={{ scale: 0 }}
                                whileInView={{ scale: 1 }}
                                transition={{ type: "spring", stiffness: 200 }}
                                className="inline-flex items-center gap-3 bg-gradient-to-r from-[#0C4550] to-[#1a5f6c] px-6 py-3 rounded-full mb-6 shadow-xl"
                            >
                                <Users className="text-white" size={20} />
                                <span className="text-sm font-bold text-white uppercase tracking-wider">
                                    STILL HAVE QUESTIONS?
                                </span>
                            </motion.div>

                            {/* Heading */}
                            <h3 className="text-3xl md:text-4xl font-extrabold text-white mb-5 drop-shadow-lg">
                                Need personalized assistance?
                            </h3>

                            {/* Description */}
                            <p className="text-white/90 mb-8 max-w-2xl mx-auto text-lg font-medium leading-relaxed drop-shadow-sm">
                                Our expert team is ready to help you with
                                <span className="font-bold text-white"> bulk SMS services</span>,
                                <span className="font-bold text-white"> pricing</span>, and
                                <span className="font-bold text-white"> implementation</span>.
                            </p>

                            {/* CTA Buttons */}
                            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="
            inline-flex items-center justify-center gap-4
            bg-gradient-to-r from-[#0C4550] to-[#1a5f6c]
            hover:from-[#083038] hover:to-[#0C4550]
            text-white font-bold
            py-4 px-8 rounded-xl
            transition-all shadow-xl hover:shadow-2xl
          "
                                >
                                    <Phone size={22} />
                                    <div className="text-left">
                                        <div className="text-xs font-semibold uppercase tracking-wider opacity-90">
                                            Call us anytime
                                        </div>
                                        <div className="text-lg font-bold tracking-wide">
                                            +91-9717055088
                                        </div>
                                    </div>
                                </motion.button>

                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="
            inline-flex items-center justify-center gap-4
            bg-white/95 text-[#0C4550]
            border border-white/40
            hover:bg-white
            font-bold
            py-4 px-8 rounded-xl
            transition-all shadow-xl hover:shadow-2xl
          "
                                >
                                    <Mail size={22} />
                                    <div className="text-left">
                                        <div className="text-xs font-semibold uppercase tracking-wider">
                                            Email support
                                        </div>
                                        <div className="text-lg font-bold">
                                           info@deetyasoft.com
                                        </div>
                                    </div>
                                </motion.button>
                            </div>

                            {/* Footer Note */}
                            <p className="mt-8 text-medium text-white/90 drop-shadow-sm">
                                Average response time:
                                <span className="font-bold text-[#C5F637] ml-1">
                                    Under 5 minutes
                                </span>
                            </p>
                        </div>
                    </div>
                </motion.div>


            </div>
        </section>
    );
};

// --- Main Component ---

export const Pricing = () => {
    // 1. Background Pattern Styles
    const bgPuzzleStyle = {
        backgroundImage: `
      linear-gradient(#0C4550 1px, transparent 1px),
      linear-gradient(90deg, #0C4550 1px, transparent 1px)
    `,
        backgroundSize: '25px 25px',
    };

    const bgHoneycombStyle = {
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='42' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg id='hexagons' fill='%230C4550' fill-opacity='0.25' fill-rule='nonzero'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    };

    const plans = [
        {
            title: "Promotional",
            price: "₹ 0.12",
            icon: Rocket,
            recommended: false,
            features: [
                { text: "Delivery between 9 AM to 9 PM", included: true },
                { text: "Dynamic Sender ID (6 Characters)", included: true },
                { text: "Real-time Delivery Reports", included: true },
                { text: "Smart Link Tracking", included: true },
                { text: "API Access", included: false },
                { text: "Priority Support", included: false },
            ]
        },
        {
            title: "Transactional",
            price: "₹ 0.15",
            icon: MessageCircle,
            recommended: true,
            features: [
                { text: "24x7 Delivery (Round the Clock)", included: true },
                { text: "Instant Delivery (Priority Routes)", included: true },
                { text: "Secure API for OTP/Alerts", included: true },
                { text: "Unlimited Validity", included: true },
                { text: "100% Delivery Guarantee", included: true },
                { text: "Dedicated Account Manager", included: true },
            ]
        }
    ];

    return (
        <div className="font-sans">
            {/* --- SECTION 1: Standard Pricing --- */}
            <section className="relative w-full min-h-screen bg-white overflow-hidden py-12 px-4 sm:px-5 lg:px-6">

                {/* Background Wrapper */}
                <div className="absolute inset-0 z-0 pointer-events-none">
                    {/* LEFT PATTERN: Puzzle Grid */}
                    <div
                        className="absolute inset-0 opacity-[0.06]"
                        style={{
                            ...bgPuzzleStyle,
                            maskImage: 'linear-gradient(to right, black 0%, transparent 60%)',
                            WebkitMaskImage: 'linear-gradient(to right, black 0%, transparent 60%)'
                        }}
                    ></div>

                    {/* RIGHT PATTERN: Honeycomb */}
                    <div
                        className="absolute inset-0 opacity-[0.1]"
                        style={{
                            ...bgHoneycombStyle,
                            maskImage: 'linear-gradient(to left, black 0%, transparent 60%)',
                            WebkitMaskImage: 'linear-gradient(to left, black 0%, transparent 60%)'
                        }}
                    ></div>
                </div>

                <div className="max-w-6xl mx-auto relative z-10">

                    {/* Header Section */}
                    <div className="text-center mb-12 space-y-3">
                        <motion.div
                            initial={{ opacity: 0, y: -15 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="inline-block"
                        >
                            <span className="bg-orange-50 text-[#F46653] font-bold px-3 py-1.5 rounded-full text-xs border border-orange-100">
                                TRANSPARENT PRICING
                            </span>
                        </motion.div>

                        <h2 className="text-3xl md:text-4xl lg:text-6xl font-extrabold text-gray-800 leading-snug">
                            Plans that scale with your{' '}
                            <span
                                className="inline-block text-[#F46653] cursor-default transition-transform hover:scale-105"
                                style={{
                                    textShadow: '1px 1px 0 #c0392b, 1.5px 1.5px 0 #c0392b'
                                }}
                            >
                                Business
                            </span>
                        </h2>
                        <p className="text-lg text-gray-600 max-w-xl mx-auto">
                            Simple, pay-as-you-go pricing. No hidden fees.
                            Choose the perfect Eurofox package for your needs.
                        </p>
                    </div>

                    {/* Main Content Split */}
                    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-stretch">

                        {/* LEFT SIDE: Professional Image & Value Prop */}
                        {/* Smooth Left-to-Right Animation */}
                        <motion.div
                            variants={imageLeftToRightVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            className="lg:w-5/12 relative rounded-2xl overflow-hidden shadow-xl min-h-[400px] lg:min-h-0 group"
                        >
                            <img
                                src={SIDE_IMAGE}
                                alt="Business Growth"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-600 group-hover:scale-105"
                            />

                            {/* Dark Gradient Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0C4550] via-[#0C4550]/80 to-transparent opacity-95"></div>

                            {/* Content Overlay */}
                            <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                                <div className="mb-5 space-y-3">
                                    <div className="w-12 h-12 bg-[#F46653] rounded-xl flex items-center justify-center shadow-md transform -rotate-3">
                                        <Building2 size={22} className="text-white" />
                                    </div>
                                    <h3 className="text-2xl font-bold">Enterprise Needs?</h3>
                                    <p className="text-gray-200 leading-relaxed text-base">
                                        Need more than 1 Lakh SMS per month? We offer tailored solutions for high-volume enterprises with dedicated SMPP connectivity.
                                    </p>
                                </div>

                                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-colors cursor-pointer">
                                    <div className="flex items-center gap-3">
                                        <div className="bg-white rounded-full p-2.5 shadow-sm">
                                            <Headphones size={20} className="text-[#0C4550]" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-300 font-medium uppercase tracking-wide">Talk to Sales</p>
                                            <p className="font-bold text-lg tracking-wide">+91-9717055088</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* RIGHT SIDE: Pricing Cards */}
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            className="lg:w-7/12 grid grid-cols-1 md:grid-cols-2 gap-5"
                        >
                            {plans.map((plan, index) => (
                                <PricingCard
                                    key={index}
                                    title={plan.title}
                                    price={plan.price}
                                    features={plan.features}
                                    recommended={plan.recommended}
                                    icon={plan.icon}
                                />
                            ))}
                        </motion.div>

                    </div>
                </div>
            </section>

            {/* --- SECTION 2: Interactive Calculator --- */}
            <InteractiveCalculator />

            {/* --- SECTION 3: Payment Methods --- */}
            <PaymentMethods />

            {/* --- SECTION 4: FAQ Section --- */}
            <FAQSection />
        </div>
    );
};

export default Pricing;